// FE-PINV-065 — TanStack Query hooks for SavedView CRUD.
//
// Mutations invalidate the list cache so the dropdown always
// reflects current state. The pin mutation does optimistic UI per
// the spec: flip the new default on locally, rollback on 4xx and
// surface the error in a CrmFormBanner.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  createSavedView,
  deleteSavedView,
  fetchSavedViews,
  patchSavedView,
  pinSavedView,
} from "./savedViewApi";
import type {
  CreateSavedViewPayload,
  PatchSavedViewPayload,
  SavedView,
  SavedViewListResponse,
} from "./savedViewTypes";


export const SAVED_VIEWS_QUERY_KEY = "pipeline-saved-views" as const;


export function useSavedViewsQuery(): UseQueryResult<SavedViewListResponse> {
  return useQuery({
    queryKey: [SAVED_VIEWS_QUERY_KEY],
    queryFn: ({ signal }) => fetchSavedViews({ signal }),
  });
}


export function useCreateSavedViewMutation(): UseMutationResult<
  SavedView, Error, CreateSavedViewPayload
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => createSavedView(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [SAVED_VIEWS_QUERY_KEY] });
    },
  });
}


export function usePatchSavedViewMutation(): UseMutationResult<
  SavedView, Error, { id: string; patch: PatchSavedViewPayload }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }) => patchSavedView(id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [SAVED_VIEWS_QUERY_KEY] });
    },
  });
}


export function useDeleteSavedViewMutation(): UseMutationResult<
  void, Error, string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteSavedView(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [SAVED_VIEWS_QUERY_KEY] });
    },
  });
}


// Optimistic pin: flip ``is_pinned_default`` locally on the new
// default + unset on every other row. Rollback the entire cache to
// its pre-mutation snapshot on error.
export function usePinSavedViewMutation(): UseMutationResult<
  SavedView, Error, string, { previous?: SavedViewListResponse }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => pinSavedView(id),
    onMutate: async (id: string) => {
      await qc.cancelQueries({ queryKey: [SAVED_VIEWS_QUERY_KEY] });
      const previous = qc.getQueryData<SavedViewListResponse>([
        SAVED_VIEWS_QUERY_KEY,
      ]);
      if (previous) {
        qc.setQueryData<SavedViewListResponse>(
          [SAVED_VIEWS_QUERY_KEY],
          {
            ...previous,
            rows: previous.rows.map((r) => ({
              ...r, is_pinned_default: r.id === id,
            })),
          },
        );
      }
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData([SAVED_VIEWS_QUERY_KEY], ctx.previous);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: [SAVED_VIEWS_QUERY_KEY] });
    },
  });
}
